import 'chart.js/auto'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  createLabel as createLabelRecord,
  deleteSchool,
  fetchDeviceInterfaces,
  fetchLabels,
  fetchRegionsAndCities,
  fetchSchoolOverview,
  updateDeviceInfo,
  updateDeviceLabel,
  updateObjectInfo,
  updateObjectVisible,
} from '../api/dashboard'
import DeleteSchoolConfirmModal from '../components/DeleteSchoolConfirmModal'
import Header from '../components/Header'
import Sidebar from '../components/Sidebar'
import SpeedtestModalWebSocket from '../components/SpeedtestModalWebSocket'
import { AlertsTable, MapSection } from '../components/dashboard'
import RangeSwitch, { schoolTimeRanges } from '../components/dashboard/RangeSwitch'
import HikvisionDoorsSection from '../components/dashboard/HikvisionDoorsSection'
import HikvisionPanicSection from '../components/dashboard/HikvisionPanicSection'
import HikvisionNvrSection from '../components/dashboard/HikvisionNvrSection'
import SchoolEquipmentTiles from '../components/dashboard/SchoolEquipmentTiles'
import HistoryGraph from '../components/dashboard/HistoryGraph'
import { EditButtons, MetaField } from '../components/dashboard/MetaField'
import useSchoolDashboardData from '../hooks/useSchoolDashboardData'
import { DASHBOARD_TIME_RANGES } from '../utils/timeRanges'

const buildMarkersFromOverview = (overview) => {
  if (!overview || !overview.lat || !overview.lon) {
    return []
  }

  return [
    {
      name: overview.school,
      lat: overview.lat,
      lon: overview.lon,
      status: overview.status,
      region: overview.region || null,
      phone: overview.phone || null,
      address: overview.address || null,
      photo_url: overview.photo_url || null,
      ip: overview.ip || overview.ip_address || null,
      additional_url: overview.additional_url || null,
      label_id: overview.label?.id ?? null,
      label_name: overview.label?.label ?? null,
      label_color: overview.label?.color ?? null,
    },
  ]
}

const formatSpeed = (value) => {
  if (value == null) return '—'
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return value
  return `${numeric.toFixed(2)} Мбит/с`
}

const formatLoss = (row) => {
  const measured = row?.download_mbps != null || row?.upload_mbps != null
  if (!measured) return '—'
  if (row.packet_loss == null || row.packet_loss === '') return '—'
  const numeric = Number(row.packet_loss)
  if (!Number.isFinite(numeric)) return '—'
  return `${numeric}%`
}

const formatTimestamp = (value) => {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('ru-KZ', {
      timeZone: 'Asia/Almaty',
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch (error) {
    return value
  }
}

const buildDeviceChartData = (series, t, historyRange) => {
  // Получаем конфигурацию временного диапазона
  const rangeConfig = DASHBOARD_TIME_RANGES[historyRange]
  if (!rangeConfig) {
    return null
  }

  const { duration, step } = rangeConfig
  const now = Math.floor(Date.now() / 1000)
  const end = now
  const start = end - duration

  // Создаем Map для быстрого поиска данных по timestamp
  const inDataMap = new Map()
  const outDataMap = new Map()

  if (series?.in?.length) {
    series.in.forEach((point) => {
      inDataMap.set(point[0], parseFloat(point[1]))
    })
  }

  if (series?.out?.length) {
    series.out.forEach((point) => {
      outDataMap.set(point[0], parseFloat(point[1]))
    })
  }

  // Генерируем полный диапазон временных точек
  const timestamps = []
  for (let ts = start; ts <= end; ts += step) {
    timestamps.push(ts)
  }

  // Определяем формат метки в зависимости от диапазона времени
  const getDateFormatOptions = (range) => {
    if (range === '7d' || range === '30d') {
      return {
        timeZone: 'Asia/Almaty',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }
    }
    return {
      timeZone: 'Asia/Almaty',
      hour: '2-digit',
      minute: '2-digit',
    }
  }

  const formatOptions = getDateFormatOptions(historyRange)

  // Функция для поиска ближайшего значения (с допуском в 1 step)
  const findValue = (dataMap, targetTs) => {
    // Сначала пробуем точное совпадение
    if (dataMap.has(targetTs)) {
      return dataMap.get(targetTs)
    }
    // Ищем в диапазоне ±step/2
    const tolerance = step / 2
    for (const [ts, value] of dataMap.entries()) {
      if (Math.abs(ts - targetTs) <= tolerance) {
        return value
      }
    }
    return null
  }

  const inData = timestamps.map((ts) => findValue(inDataMap, ts))
  const outData = timestamps.map((ts) => findValue(outDataMap, ts))

  // Проверяем, есть ли хоть какие-то данные
  const hasAnyData = inData.some((v) => v !== null) || outData.some((v) => v !== null)
  if (!hasAnyData) {
    return null
  }

  return {
    labels: timestamps.map((ts) =>
      new Date(ts * 1000).toLocaleString('ru-KZ', formatOptions),
    ),
    rawTimestamps: timestamps,
    datasets: [
      {
        label: t('dashboard.incomingTraffic'),
        data: inData,
        spanGaps: false, // Не соединять линией через пустые значения
      },
      {
        label: t('dashboard.outgoingTraffic'),
        data: outData,
        spanGaps: false,
      },
    ],
  }
}

const LogsTable = ({ logs, isLoading, page, totalPages, onPageChange, t }) => {
  if (isLoading) {
    return (
      <div className="flex h-60 items-center justify-center rounded-3xl bg-surface-2">
        <div className="flex items-center gap-3 text-sm text-muted-300">
          <span className="material-icons animate-spin text-brand-300">progress_activity</span>
          {t('schoolDashboard.loadingLogs')}
        </div>
      </div>
    )
  }

  if (!logs.length) {
    return (
      <div className="flex h-60 items-center justify-center rounded-3xl bg-surface-2 text-sm text-muted-300">
        {t('schoolDashboard.noLogsForPeriod')}
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-3xl bg-surface-2">
      <div className="grid grid-cols-12 gap-0 bg-surface-1 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-400">
        <span className="col-span-3">{t('schoolDashboard.dateTime')}</span>
        <span className="col-span-9">{t('schoolDashboard.message')}</span>
      </div>
      <div className="divide-y divide-white/[0.04]">
        {logs.map((log, index) => (
          <div key={`${log.timestamp}-${index}`} className="grid grid-cols-12 gap-3 px-4 py-3 text-sm text-muted-200">
            <span className="col-span-3 text-xs text-muted-400">{formatTimestamp(log.timestamp)}</span>
            <span className="col-span-9 text-muted-100">{log.message}</span>
          </div>
        ))}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 text-xs text-muted-400">
          <span>
            {t('schoolDashboard.pageOf', { current: page, total: totalPages })}
          </span>
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-300 transition hover:bg-brand-400/15 hover:text-white disabled:opacity-40"
              disabled={page === 1}
            >
              <span className="material-icons text-sm">chevron_left</span>
            </button>
            <button
              type="button"
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-300 transition hover:bg-brand-400/15 hover:text-white disabled:opacity-40"
              disabled={page === totalPages}
            >
              <span className="material-icons text-sm">chevron_right</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const SpeedtestTable = ({ results, isLoading, page, totalPages, onPageChange, t }) => {
  if (isLoading && !results.length) {
    return (
      <div className="flex h-60 items-center justify-center rounded-3xl bg-surface-2 text-sm text-muted-300">
        <div className="flex items-center gap-3">
          <span className="material-icons animate-spin text-brand-300">progress_activity</span>
          {t('schoolDashboard.loadingSpeedtestResults')}
        </div>
      </div>
    )
  }

  if (!results.length) {
    return (
      <div className="flex h-60 items-center justify-center rounded-3xl bg-surface-2 text-sm text-muted-300">
        {t('schoolDashboard.noSpeedtestMeasurements')}
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-3xl bg-surface-2">
      <div className="grid grid-cols-12 gap-3 px-4 py-3 text-xs text-muted-500">
        <span className="col-span-3">{t('schoolDashboard.date')}</span>
        <span className="col-span-4">{t('schoolDashboard.download')}</span>
        <span className="col-span-4">{t('schoolDashboard.upload')}</span>
        <span className="col-span-1">{t('schoolDashboard.loss')}</span>
      </div>
      <div className={`divide-y divide-white/[0.04] ${isLoading ? 'pointer-events-none opacity-50' : ''}`}>
        {results.map((row) => (
          <div key={`${row.timestamp}-${row.download_mbps}-${row.upload_mbps}`} className="grid grid-cols-12 gap-3 px-4 py-3 text-sm text-muted-200">
            <span className="col-span-3 text-xs text-muted-400">{formatTimestamp(row.timestamp)}</span>
            <span className="col-span-4 text-accent-cyan">{formatSpeed(row.download_mbps)}</span>
            <span className="col-span-4 text-accent-pink">{formatSpeed(row.upload_mbps)}</span>
            <span className="col-span-1 text-muted-300">{formatLoss(row)}</span>
          </div>
        ))}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 text-xs text-muted-400">
          <span>
            {t('schoolDashboard.pageOf', { current: page, total: totalPages })}
          </span>
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-300 transition hover:bg-brand-400/15 hover:text-white disabled:opacity-40"
              disabled={isLoading || page === 1}
            >
              <span className="material-icons text-sm">chevron_left</span>
            </button>
            <button
              type="button"
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-300 transition hover:bg-brand-400/15 hover:text-white disabled:opacity-40"
              disabled={isLoading || page === totalPages}
            >
              <span className="material-icons text-sm">chevron_right</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const SchoolDashboard = ({ setIsAuthenticated, onLogout, userRole }) => {
  const { t } = useTranslation();
  const { state } = useLocation()
  const navigate = useNavigate()
  const { ip: routeIp } = useParams()
  const defaultTimeRanges = schoolTimeRanges(t);
  const [isSidebarVisible, setSidebarVisible] = useState(true)
  const [historyRange, setHistoryRange] = useState('24h')
  const [logsRange, setLogsRange] = useState('24h')
  const [historySeries, setHistorySeries] = useState(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [speedtestModalOpen, setSpeedtestModalOpen] = useState(false)
  const [isEditingIp, setIsEditingIp] = useState(false)
  const [isEditingSpeedtestTarget, setIsEditingSpeedtestTarget] = useState(false)
  const [isEditingVlan, setIsEditingVlan] = useState(false)
  const [isEditingSchool, setIsEditingSchool] = useState(false)
  const [isEditingRegion, setIsEditingRegion] = useState(false)
  const [isEditingCity, setIsEditingCity] = useState(false)
  const [isEditingAdditionalUrl, setIsEditingAdditionalUrl] = useState(false)
  const [isEditingPhotoUrl, setIsEditingPhotoUrl] = useState(false)
  const [editingIpValue, setEditingIpValue] = useState('')
  const [editingSpeedtestTargetValue, setEditingSpeedtestTargetValue] = useState('')
  const [editingVlanValue, setEditingVlanValue] = useState('')
  const [editingSchoolValue, setEditingSchoolValue] = useState('')
  const [editingRegionValue, setEditingRegionValue] = useState('')
  const [editingCityValue, setEditingCityValue] = useState('')
  const [editingAdditionalUrlValue, setEditingAdditionalUrlValue] = useState('')
  const [editingPhotoUrlValue, setEditingPhotoUrlValue] = useState('')
  const [regionsList, setRegionsList] = useState([])
  const [citiesList, setCitiesList] = useState([])
  const [regionsLoading, setRegionsLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [updateError, setUpdateError] = useState('')
  const [updateSuccess, setUpdateSuccess] = useState('')
  const [visibleLoading, setVisibleLoading] = useState(false)
  const [interfaces, setInterfaces] = useState([])
  const [interfacesLoading, setInterfacesLoading] = useState(false)
  const [isEditingLabel, setIsEditingLabel] = useState(false)
  const [labels, setLabels] = useState([])
  const [labelsLoading, setLabelsLoading] = useState(false)
  const [selectedLabelId, setSelectedLabelId] = useState('')
  const [newLabelName, setNewLabelName] = useState('')
  const [newLabelColor, setNewLabelColor] = useState('#6e58ff')
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  const deviceIp = state?.ip || routeIp || state?.device?.ip || ''
  
  // Проверка роли для редактирования (только admin или engineer)
  const canEdit = userRole === 'admin' || userRole === 'engineer'


  const {
    overview,
    overviewLoading,
    utilizationData,
    utilizationLoading,
    alerts,
    alertsLoading,
    alertsTotalCount,
    alertsPage,
    alertsTotalPages,
    alertsPageSize,
    setAlertsPage,
    setAlertsPageSize,
    logs,
    logsLoading,
    logsPage,
    logsTotalPages,
    setLogsPage,
    speedtests,
    speedtestsLoading,
    speedtestsPage,
    speedtestsTotalPages,
    setSpeedtestsPage,
    acknowledgeAlert,
    acknowledgeLoading,
    fetchDeviceSeries,
    reloadOverview,
  } = useSchoolDashboardData(deviceIp, { utilizationRange: historyRange, logsRange })

  const markers = useMemo(() => buildMarkersFromOverview(overview), [overview])

  const handleBack = () => {
    navigate('/dashboard')
  }

  // handleSpeedtestStart больше не нужен - используем WebSocket версию

  const handleOpenRuijie = () => {
    if (!deviceIp) return
    
    // Используем additional_url из данных школы, если он есть, иначе fallback на дефолтный URL
    const url = overview?.additional_url || 'https://sapa.aiqyn-shym.kz/macc5/adminIntl/#/monitor_project_workbarn_menu'
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  useEffect(() => {
    if (!deviceIp) return

    let isCancelled = false
    const loadHistory = async () => {
      setHistoryLoading(true)
      try {
        const series = await fetchDeviceSeries(deviceIp, historyRange)
        if (isCancelled) return
        setHistorySeries(buildDeviceChartData(series, t, historyRange))
      } catch (error) {
        if (!isCancelled) {
          setHistorySeries(null)
        }
      } finally {
        if (!isCancelled) {
          setHistoryLoading(false)
        }
      }
    }

    loadHistory()

    return () => {
      isCancelled = true
    }
  }, [deviceIp, historyRange, fetchDeviceSeries, t])

  // Загрузка списка регионов и городов
  const loadRegionsAndCities = async () => {
    setRegionsLoading(true)
    try {
      const data = await fetchRegionsAndCities()
      setRegionsList(data.regions || [])
      setCitiesList(data.cities || [])
    } catch (error) {
      console.error('Ошибка загрузки регионов и городов:', error)
      setRegionsList([])
      setCitiesList([])
    } finally {
      setRegionsLoading(false)
    }
  }

  // Загружаем регионы и города при монтировании компонента
  useEffect(() => {
    loadRegionsAndCities()
  }, [])

  if (!deviceIp) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-surface-base text-muted-100">
        <div className="rounded-3xl border border-surface-border/40 bg-surface-raised/75 p-8 text-center shadow-elevated">
          <h1 className="font-display text-2xl text-white">{t('schoolDashboard.schoolNotSelected')}</h1>
          <p className="mt-2 text-sm text-muted-300">{t('schoolDashboard.tryOpenFromList')}</p>
          <button
            type="button"
            onClick={handleBack}
            className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-brand-400/20 px-4 py-2 text-sm font-semibold text-brand-100 transition hover:bg-brand-400/30 hover:text-white"
          >
            <span className="material-icons text-sm">arrow_back</span>
            {t('schoolDashboard.toDashboard')}
          </button>
        </div>
      </div>
    )
  }

  const headerTitle = overview?.school || state?.school || t('schoolDashboard.object')
  const stability = overview?.connectionStability
  const stabilityProblem = Boolean(stability?.type && stability.type !== 'stable')
  const lastSpeedtest = overview?.lastSpeedtest || state?.overview?.lastSpeedtest

  const handleStartEditIp = () => {
    setEditingIpValue(overview?.ip || deviceIp)
    setIsEditingIp(true)
    setUpdateError('')
    setUpdateSuccess('')
  }

  const handleStartEditSpeedtestTarget = () => {
    setEditingSpeedtestTargetValue(overview?.speedtestTargetServer ?? '')
    setIsEditingSpeedtestTarget(true)
    setUpdateError('')
    setUpdateSuccess('')
  }

  const handleSaveSpeedtestTarget = async () => {
    setUpdateLoading(true)
    setUpdateError('')
    setUpdateSuccess('')

    try {
      await updateDeviceInfo(deviceIp, {
        speedtestTargetServer: editingSpeedtestTargetValue.trim() || null,
      })
      setUpdateSuccess(t('schoolDashboard.speedtestTargetUpdated'))
      setIsEditingSpeedtestTarget(false)
      reloadOverview()
      setTimeout(() => setUpdateSuccess(''), 3000)
    } catch (error) {
      setUpdateError(error.response?.data?.message || t('schoolDashboard.failedToUpdateSpeedtestTarget'))
      setTimeout(() => setUpdateError(''), 5000)
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleStartEditVlan = async () => {
    setEditingVlanValue(overview?.eshdiVlanId?.toString() || '')
    setIsEditingVlan(true)
    setUpdateError('')
    setUpdateSuccess('')
    setInterfacesLoading(true)
    setInterfaces([])
    
    try {
      const result = await fetchDeviceInterfaces(deviceIp)
      
      // Проверяем разные варианты структуры ответа
      let interfacesList = []
      if (Array.isArray(result?.interfaces)) {
        interfacesList = result.interfaces
      } else if (Array.isArray(result?.data?.interfaces)) {
        interfacesList = result.data.interfaces
      } else if (Array.isArray(result)) {
        interfacesList = result
      }
      
      setInterfaces(interfacesList)
    } catch (error) {
      // Не показываем ошибку, просто используем fallback на input
      setInterfaces([])
    } finally {
      setInterfacesLoading(false)
    }
  }

  const handleCancelEdit = () => {
    setIsEditingIp(false)
    setIsEditingSpeedtestTarget(false)
    setIsEditingVlan(false)
    setIsEditingLabel(false)
    setIsEditingSchool(false)
    setIsEditingRegion(false)
    setIsEditingCity(false)
    setIsEditingAdditionalUrl(false)
    setIsEditingPhotoUrl(false)
    setEditingIpValue('')
    setEditingSpeedtestTargetValue('')
    setEditingVlanValue('')
    setEditingSchoolValue('')
    setEditingRegionValue('')
    setEditingCityValue('')
    setEditingAdditionalUrlValue('')
    setEditingPhotoUrlValue('')
    setUpdateError('')
    setUpdateSuccess('')
    setInterfaces([])
    setInterfacesLoading(false)
    setLabels([])
    setLabelsLoading(false)
    setSelectedLabelId('')
    setNewLabelName('')
    setNewLabelColor('#6e58ff')
  }

  const handleStartEditLabel = async () => {
    setIsEditingLabel(true)
    setSelectedLabelId(overview?.label?.id != null ? String(overview.label.id) : '')
    setNewLabelName('')
    setNewLabelColor('#6e58ff')
    setUpdateError('')
    setUpdateSuccess('')
    setLabelsLoading(true)
    try {
      const list = await fetchLabels()
      setLabels(Array.isArray(list) ? list : [])
    } catch (e) {
      setLabels([])
    } finally {
      setLabelsLoading(false)
    }
  }

  const handleSaveLabel = async () => {
    setUpdateLoading(true)
    setUpdateError('')
    setUpdateSuccess('')
    try {
      let idToAssign = null
      const trimmedNew = newLabelName.trim()
      if (trimmedNew) {
        const created = await createLabelRecord(trimmedNew, newLabelColor)
        if (!created?.id) {
          throw new Error('no id')
        }
        idToAssign = created.id
      } else if (selectedLabelId) {
        idToAssign = parseInt(selectedLabelId, 10)
        if (Number.isNaN(idToAssign)) {
          setUpdateError(t('schoolDashboard.labelInvalidSelection'))
          setUpdateLoading(false)
          return
        }
      } else {
        idToAssign = null
      }
      await updateDeviceLabel(deviceIp, idToAssign)
      setUpdateSuccess(t('schoolDashboard.labelUpdated'))
      setIsEditingLabel(false)
      setNewLabelName('')
      reloadOverview()
      setTimeout(() => setUpdateSuccess(''), 3000)
    } catch (error) {
      setUpdateError(error.response?.data?.message || error.message || t('schoolDashboard.labelUpdateFailed'))
      setTimeout(() => setUpdateError(''), 5000)
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleStartEditSchool = () => {
    setEditingSchoolValue(overview?.school || '')
    setIsEditingSchool(true)
    setUpdateError('')
    setUpdateSuccess('')
  }

  const handleStartEditRegion = async () => {
    setEditingRegionValue(overview?.region || '')
    setIsEditingRegion(true)
    setUpdateError('')
    setUpdateSuccess('')
    // Обновляем список регионов при открытии редактирования
    await loadRegionsAndCities()
  }

  const handleStartEditCity = async () => {
    setEditingCityValue(overview?.city || '')
    setIsEditingCity(true)
    setUpdateError('')
    setUpdateSuccess('')
    // Обновляем список городов при открытии редактирования
    await loadRegionsAndCities()
  }

  const handleStartEditAdditionalUrl = () => {
    setEditingAdditionalUrlValue(overview?.additional_url || '')
    setIsEditingAdditionalUrl(true)
    setUpdateError('')
    setUpdateSuccess('')
  }

  const handleStartEditPhotoUrl = () => {
    setEditingPhotoUrlValue(overview?.photo_url || '')
    setIsEditingPhotoUrl(true)
    setUpdateError('')
    setUpdateSuccess('')
  }

  const handleSaveSchool = async () => {
    if (!editingSchoolValue.trim()) {
      setUpdateError(t('schoolDashboard.schoolNameEmpty'))
      return
    }

    setUpdateLoading(true)
    setUpdateError('')
    setUpdateSuccess('')

    try {
      await updateObjectInfo(deviceIp, { name: editingSchoolValue.trim() })
      setUpdateSuccess(t('schoolDashboard.schoolNameUpdated'))
      setIsEditingSchool(false)
      reloadOverview()
      setTimeout(() => setUpdateSuccess(''), 3000)
    } catch (error) {
      setUpdateError(error.response?.data?.message || t('schoolDashboard.failedToUpdateSchoolName'))
      setTimeout(() => setUpdateError(''), 5000)
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleSaveRegion = async () => {
    setUpdateLoading(true)
    setUpdateError('')
    setUpdateSuccess('')

    try {
      await updateObjectInfo(deviceIp, { region: editingRegionValue.trim() || null })
      setUpdateSuccess(t('schoolDashboard.regionUpdated'))
      setIsEditingRegion(false)
      reloadOverview()
      setTimeout(() => setUpdateSuccess(''), 3000)
    } catch (error) {
      setUpdateError(error.response?.data?.message || t('schoolDashboard.failedToUpdateRegion'))
      setTimeout(() => setUpdateError(''), 5000)
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleSaveCity = async () => {
    setUpdateLoading(true)
    setUpdateError('')
    setUpdateSuccess('')

    try {
      await updateObjectInfo(deviceIp, { city: editingCityValue.trim() || null })
      setUpdateSuccess(t('schoolDashboard.cityUpdated'))
      setIsEditingCity(false)
      reloadOverview()
      setTimeout(() => setUpdateSuccess(''), 3000)
    } catch (error) {
      setUpdateError(error.response?.data?.message || t('schoolDashboard.failedToUpdateCity'))
      setTimeout(() => setUpdateError(''), 5000)
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleSavePhotoUrl = async () => {
    const trimmedUrl = editingPhotoUrlValue.trim()
    
    // Валидация URL (может быть пустым или валидным URL)
    if (trimmedUrl && trimmedUrl.length > 0) {
      try {
        new URL(trimmedUrl)
      } catch (e) {
        setUpdateError(t('schoolDashboard.invalidPhotoUrlFormat'))
        return
      }
    }

    setUpdateLoading(true)
    setUpdateError('')
    setUpdateSuccess('')

    try {
      await updateObjectInfo(deviceIp, { photo_url: trimmedUrl || null })
      setUpdateSuccess(t('schoolDashboard.photoUrlUpdated'))
      setIsEditingPhotoUrl(false)
      reloadOverview()
      setTimeout(() => setUpdateSuccess(''), 3000)
    } catch (error) {
      setUpdateError(error.response?.data?.message || t('schoolDashboard.failedToUpdatePhotoUrl'))
      setTimeout(() => setUpdateError(''), 5000)
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleSaveAdditionalUrl = async () => {
    const trimmedUrl = editingAdditionalUrlValue.trim()
    
    // Валидация URL (может быть пустым или валидным URL)
    if (trimmedUrl && trimmedUrl.length > 0) {
      try {
        new URL(trimmedUrl)
      } catch (e) {
        setUpdateError(t('schoolDashboard.invalidUrlFormat'))
        return
      }
    }

    setUpdateLoading(true)
    setUpdateError('')
    setUpdateSuccess('')

    try {
      await updateObjectInfo(deviceIp, { additional_url: trimmedUrl || null })
      setUpdateSuccess(t('schoolDashboard.additionalUrlUpdated'))
      setIsEditingAdditionalUrl(false)
      reloadOverview()
      setTimeout(() => setUpdateSuccess(''), 3000)
    } catch (error) {
      setUpdateError(error.response?.data?.message || t('schoolDashboard.failedToUpdateAdditionalUrl'))
      setTimeout(() => setUpdateError(''), 5000)
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleSaveIp = async () => {
    if (!editingIpValue.trim()) {
      setUpdateError(t('schoolDashboard.ipAddressEmpty'))
      return
    }

    // Простая валидация IP адреса
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/
    if (!ipRegex.test(editingIpValue.trim())) {
      setUpdateError(t('schoolDashboard.invalidIpFormat'))
      return
    }

    setUpdateLoading(true)
    setUpdateError('')
    setUpdateSuccess('')

    try {
      const result = await updateDeviceInfo(deviceIp, { newIp: editingIpValue.trim() })
      setUpdateSuccess(t('schoolDashboard.ipAddressUpdated'))
      setIsEditingIp(false)
      
      // Обновляем URL, если IP изменился
      const newIp = result?.ip || editingIpValue.trim()
      if (newIp !== deviceIp) {
        const newOverview = await fetchSchoolOverview(newIp)
        navigate(`/schools/${encodeURIComponent(newIp)}`, {
          state: { ip: newIp, school: newOverview?.school },
          replace: true,
        })
        // После навигации компонент перезагрузится с новым IP
      } else {
        // Если IP не изменился, просто перезагружаем данные
        reloadOverview()
      }
      
      // Автоматически скрываем сообщение об успехе через 3 секунды
      setTimeout(() => setUpdateSuccess(''), 3000)
    } catch (error) {
      setUpdateError(error.response?.data?.message || t('schoolDashboard.failedToUpdateIp'))
      setTimeout(() => setUpdateError(''), 5000)
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleSaveVlan = async () => {
    const vlanId = parseInt(editingVlanValue.trim(), 10)
    if (isNaN(vlanId) || vlanId < 0) {
      setUpdateError(t('schoolDashboard.vlanIdMustBePositive'))
      return
    }

    setUpdateLoading(true)
    setUpdateError('')
    setUpdateSuccess('')

    try {
      await updateDeviceInfo(deviceIp, { eshdiVlanId: vlanId })
      setUpdateSuccess(t('schoolDashboard.vlanIdUpdated'))
      setIsEditingVlan(false)
      
      // Перезагружаем данные
      reloadOverview()
      
      // Автоматически скрываем сообщение об успехе через 3 секунды
      setTimeout(() => setUpdateSuccess(''), 3000)
    } catch (error) {
      setUpdateError(error.response?.data?.message || t('schoolDashboard.failedToUpdateVlanId'))
      setTimeout(() => setUpdateError(''), 5000)
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleToggleVisible = async () => {
    const newVisible = !overview?.visible
    setVisibleLoading(true)
    setUpdateError('')
    setUpdateSuccess('')

    try {
      await updateObjectVisible(deviceIp, newVisible)
      setUpdateSuccess(newVisible ? t('schoolDashboard.objectShown') : t('schoolDashboard.objectHidden'))
      reloadOverview()
      setTimeout(() => setUpdateSuccess(''), 3000)
    } catch (error) {
      setUpdateError(error.response?.data?.message || t('schoolDashboard.failedToChangeVisibility'))
      setTimeout(() => setUpdateError(''), 5000)
    } finally {
      setVisibleLoading(false)
    }
  }

  const handleDeleteSchool = async () => {
    setIsDeleting(true)
    setDeleteError('')

    try {
      await deleteSchool(deviceIp)
      // После успешного удаления перенаправляем на дашборд
      navigate('/dashboard')
    } catch (error) {
      setDeleteError(error.response?.data?.message || t('errors.failedToDeleteSchool'))
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="relative flex min-h-screen flex-col overflow-clip bg-surface-base text-muted-100">
      <div className="absolute inset-0 -z-10 bg-brand-radial" />
      <Header
        isSidebarVisible={isSidebarVisible}
        toggleSidebar={() => setSidebarVisible((prev) => !prev)}
        onLogout={onLogout}
        userEmail={localStorage.getItem('userEmail') || ''}
        userRole={userRole}
      />
      <div className="flex flex-1">
        {isSidebarVisible && (
          <Sidebar
            isSidebarVisible={isSidebarVisible}
            onLogout={onLogout}
            userRole={userRole}
            onClose={() => setSidebarVisible(false)}
          />
        )}
        <main className="relative flex min-w-0 flex-1 flex-col gap-6 bg-surface-base/60 px-4 py-6 transition-all duration-300 ease-brand sm:px-6 lg:px-10">
          <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 bg-brand-linear/20 blur-3xl" />
          <div className="pointer-events-none absolute inset-y-0 right-0 -z-10 w-64 bg-brand-radial blur-3xl" />

          <div className="space-y-2">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500 transition hover:text-white"
          >
            <span className="material-icons text-[14px]">arrow_back</span>
            {t('schoolDashboard.backToList')}
          </button>

          <header className="flex flex-col gap-6 lg:flex-row lg:items-stretch lg:justify-between">
            <div className="min-w-0">
              <div className="group flex items-center gap-2">
                {isEditingSchool ? (
                  <>
                    <input
                      type="text"
                      value={editingSchoolValue}
                      onChange={(e) => setEditingSchoolValue(e.target.value)}
                      className="min-w-0 flex-1 rounded-lg bg-surface-2 px-3 py-1.5 text-2xl font-display text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 md:text-3xl"
                      disabled={updateLoading}
                      placeholder={t('schoolDashboard.schoolNamePlaceholder')}
                    />
                    <EditButtons
                      onSave={handleSaveSchool}
                      onCancel={handleCancelEdit}
                      saveDisabled={!editingSchoolValue.trim()}
                      updateLoading={updateLoading}
                    />
                  </>
                ) : (
                  <>
                    <h1 className="font-display text-3xl text-white md:text-4xl">{headerTitle}</h1>
                    <span className="inline-flex items-center gap-1.5 text-sm text-muted-300">
                      <span
                        className={`h-2 w-2 rounded-full ${
                          overviewLoading
                            ? 'bg-muted-500'
                            : overview?.status === 'up'
                              ? 'bg-positive'
                              : 'bg-critical'
                        }`}
                      />
                      {overviewLoading
                        ? t('schoolDashboard.checkingStatus')
                        : overview?.status === 'up'
                          ? t('common.online')
                          : t('common.offline')}
                    </span>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={handleStartEditSchool}
                        className="inline-flex items-center rounded p-0.5 text-muted-500 opacity-0 transition group-hover:opacity-100 hover:text-white focus:opacity-100"
                        title={t('schoolDashboard.editSchoolName')}
                      >
                        <span className="material-icons text-sm">edit</span>
                      </button>
                    )}
                  </>
                )}
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-400">
                {(overview?.city || overview?.region) && (
                  <span>{[overview?.city, overview?.region].filter(Boolean).join(', ')}</span>
                )}
                {overview?.label?.label && (
                  <>
                    {(overview?.city || overview?.region) ? <span className="text-muted-600">·</span> : null}
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: overview.label.color }} />
                      {overview.label.label}
                    </span>
                  </>
                )}
                {stabilityProblem && (
                  <>
                    <span className="text-muted-600">·</span>
                    <span className={stability.type === 'offline' ? 'text-critical' : 'text-warning'}>
                      {stability.message}
                    </span>
                  </>
                )}
                {overview?.visible === false && (
                  <>
                    <span className="text-muted-600">·</span>
                    <span className="inline-flex items-center gap-1 text-muted-500">
                      <span className="material-icons text-[14px]">visibility_off</span>
                      {t('schoolDashboard.hidden')}
                    </span>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setShowDetails((v) => !v)}
                  className="ml-3 inline-flex items-center gap-1 text-muted-500 transition hover:text-white"
                >
                  <span className="material-icons text-[16px]">{showDetails ? 'expand_less' : 'tune'}</span>
                  {showDetails ? t('schoolDashboard.hideDetails') : t('schoolDashboard.details')}
                </button>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setSpeedtestModalOpen(true)}
                  className="inline-flex items-center gap-2 rounded-xl bg-brand-400/20 px-4 py-2 text-sm font-medium text-brand-50 transition hover:bg-brand-400/30"
                >
                  <span className="material-icons text-[18px]">speed</span>
                  Speedtest
                </button>
                <span className="text-xs text-muted-500">
                  {lastSpeedtest ? (
                    lastSpeedtest.download == null && lastSpeedtest.upload == null ? (
                      <>
                        {formatTimestamp(lastSpeedtest.timestamp)} · {t('schoolDashboard.noResult')}
                      </>
                    ) : (
                      <>
                        {formatTimestamp(lastSpeedtest.timestamp)} ·{' '}
                        <span className="text-accent-cyan">↓ {lastSpeedtest.download ?? '—'}</span>{' '}
                        <span className="text-accent-pink">↑ {lastSpeedtest.upload ?? '—'}</span>
                      </>
                    )
                  ) : (
                    t('schoolDashboard.noMeasurements')
                  )}
                </span>
                <button
                  type="button"
                  onClick={handleOpenRuijie}
                  className="inline-flex items-center gap-2 rounded-xl bg-white/[0.04] px-4 py-2 text-sm text-muted-200 transition hover:bg-white/[0.08] hover:text-white"
                >
                  <span className="material-icons text-[18px] text-accent-cyan">open_in_new</span>
                  {t('schoolDashboard.scs')}
                </button>
              </div>
            </div>

            <div className="flex w-full shrink-0 lg:w-[38rem]">
              <SchoolEquipmentTiles
                routerIp={deviceIp}
                onFire={
                  overview?.firePanel?.state === 'on_fire'
                  || overview?.firePanel?.onFire === true
                }
              />
            </div>
          </header>
          </div>

              {showDetails && (
              <div className="grid max-w-4xl grid-cols-1 gap-x-12 gap-y-2 rounded-2xl bg-surface-1 p-5 sm:grid-cols-2">
                <MetaField
                  label="IP"
                  editing={isEditingIp}
                  canEdit={canEdit}
                  onStartEdit={handleStartEditIp}
                  editTitle={t('schoolDashboard.editIpAddress')}
                  mono
                  editor={
                    <>
                      <input
                        type="text"
                        value={editingIpValue}
                        onChange={(e) => setEditingIpValue(e.target.value)}
                        className="w-full min-w-0 rounded-lg bg-surface-2 px-2 py-1 font-mono text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                        disabled={updateLoading}
                      />
                      <EditButtons onSave={handleSaveIp} onCancel={handleCancelEdit} updateLoading={updateLoading} />
                    </>
                  }
                >
                  {overview?.ip || deviceIp}
                </MetaField>

                <MetaField
                  label={t('schoolDashboard.speedtestTargetServer')}
                  editing={isEditingSpeedtestTarget}
                  canEdit={canEdit}
                  mono
                  onStartEdit={handleStartEditSpeedtestTarget}
                  editTitle={t('schoolDashboard.editSpeedtestTarget')}
                  editor={
                    <>
                      <input
                        type="text"
                        value={editingSpeedtestTargetValue}
                        onChange={(e) => setEditingSpeedtestTargetValue(e.target.value)}
                        placeholder={t('schoolDashboard.speedtestTargetPlaceholder')}
                        className="w-full min-w-0 rounded-lg bg-surface-2 px-2 py-1 text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                        disabled={updateLoading}
                      />
                      <EditButtons onSave={handleSaveSpeedtestTarget} onCancel={handleCancelEdit} updateLoading={updateLoading} />
                    </>
                  }
                >
                  {overview?.speedtestTargetServer ?? '—'}
                </MetaField>

                <MetaField
                  label={t('schoolDashboard.eshdiVlanId')}
                  editing={isEditingVlan}
                  canEdit={canEdit}
                  onStartEdit={handleStartEditVlan}
                  editTitle={t('schoolDashboard.editVlanId')}
                  editor={
                    <>
                      {interfacesLoading ? (
                        <span className="text-xs text-muted-400">{t('schoolDashboard.loadingInterfaces')}</span>
                      ) : Array.isArray(interfaces) && interfaces.length > 0 ? (
                        <select
                          value={editingVlanValue}
                          onChange={(e) => setEditingVlanValue(e.target.value)}
                          className="w-32 rounded-lg bg-surface-2 px-2 py-1 text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                          disabled={updateLoading}
                        >
                          <option value="">{t('schoolDashboard.selectInterface')}</option>
                          {interfaces.map((iface) => (
                            <option key={iface} value={iface.toString()}>
                              {t('schoolDashboard.interface')} {iface}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="number"
                          value={editingVlanValue}
                          onChange={(e) => setEditingVlanValue(e.target.value)}
                          className="w-20 rounded-lg bg-surface-2 px-2 py-1 text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                          disabled={updateLoading}
                          min="0"
                          placeholder={t('schoolDashboard.enterId')}
                        />
                      )}
                      <EditButtons onSave={handleSaveVlan} onCancel={handleCancelEdit} saveDisabled={!editingVlanValue} updateLoading={updateLoading} />
                    </>
                  }
                >
                  {overview?.eshdiVlanId ?? '—'}
                </MetaField>

                <MetaField
                  label={t('schoolDashboard.label')}
                  editing={isEditingLabel}
                  canEdit={canEdit}
                  onStartEdit={handleStartEditLabel}
                  editTitle={t('schoolDashboard.editLabel')}
                  editor={
                    labelsLoading ? (
                      <span className="text-xs text-muted-400">{t('schoolDashboard.loadingLabels')}</span>
                    ) : (
                      <>
                        <select
                          value={selectedLabelId}
                          onChange={(e) => setSelectedLabelId(e.target.value)}
                          className="min-w-[140px] rounded-lg bg-surface-2 px-2 py-1 text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                          disabled={updateLoading}
                        >
                          <option value="">{t('schoolDashboard.labelNone')}</option>
                          {labels.map((lb) => (
                            <option key={lb.id} value={String(lb.id)}>
                              {lb.label}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={newLabelName}
                          onChange={(e) => setNewLabelName(e.target.value)}
                          placeholder={t('schoolDashboard.newLabelName')}
                          className="min-w-[100px] rounded-lg bg-surface-2 px-2 py-1 text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                          disabled={updateLoading}
                        />
                        <input
                          type="color"
                          value={newLabelColor}
                          onChange={(e) => setNewLabelColor(e.target.value)}
                          className="h-8 w-10 cursor-pointer rounded bg-transparent p-0.5"
                          title={t('schoolDashboard.labelColor')}
                          disabled={updateLoading}
                        />
                        <EditButtons onSave={handleSaveLabel} onCancel={handleCancelEdit} updateLoading={updateLoading} />
                      </>
                    )
                  }
                >
                  {overview?.label?.label ? (
                    <span className="inline-flex items-center gap-1.5 text-sm text-white">
                      <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: overview.label.color }} />
                      {overview.label.label}
                    </span>
                  ) : (
                    '—'
                  )}
                </MetaField>

                {canEdit && (
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="w-36 shrink-0 text-xs text-muted-500">{t('schoolDashboard.visibility')}</span>
                    <button
                      type="button"
                      onClick={handleToggleVisible}
                      disabled={visibleLoading}
                      className={`inline-flex items-center gap-1.5 text-sm transition ${
                        overview?.visible === false ? 'text-muted-400' : 'text-positive'
                      } disabled:opacity-50`}
                      title={overview?.visible === false ? t('schoolDashboard.showSchool') : t('schoolDashboard.hideSchool')}
                    >
                      {visibleLoading ? (
                        <span className="material-icons animate-spin text-sm">refresh</span>
                      ) : (
                        <>
                          <span className="material-icons text-sm">
                            {overview?.visible === false ? 'visibility_off' : 'visibility'}
                          </span>
                          {overview?.visible === false ? t('schoolDashboard.hidden') : t('schoolDashboard.visible')}
                        </>
                      )}
                    </button>
                  </div>
                )}

                <MetaField
                  label={t('schoolDashboard.city')}
                  editing={isEditingCity}
                  canEdit={canEdit}
                  onStartEdit={handleStartEditCity}
                  editTitle={t('schoolDashboard.editCity')}
                  editor={
                    <>
                      {regionsLoading ? (
                        <span className="material-icons animate-spin text-sm text-muted-400">refresh</span>
                      ) : (
                        <select
                          value={editingCityValue}
                          onChange={(e) => setEditingCityValue(e.target.value)}
                          className="min-w-40 rounded-lg bg-surface-2 px-2 py-1 text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                          disabled={updateLoading}
                        >
                          <option value="">{t('schoolDashboard.selectCity')}</option>
                          {citiesList.map((city) => (
                            <option key={city} value={city}>{city}</option>
                          ))}
                        </select>
                      )}
                      <EditButtons onSave={handleSaveCity} onCancel={handleCancelEdit} updateLoading={updateLoading} />
                    </>
                  }
                >
                  {overview?.city || '—'}
                </MetaField>

                <MetaField
                  label={t('schoolDashboard.region')}
                  editing={isEditingRegion}
                  canEdit={canEdit}
                  onStartEdit={handleStartEditRegion}
                  editTitle={t('schoolDashboard.editRegion')}
                  editor={
                    <>
                      {regionsLoading ? (
                        <span className="material-icons animate-spin text-sm text-muted-400">refresh</span>
                      ) : (
                        <select
                          value={editingRegionValue}
                          onChange={(e) => setEditingRegionValue(e.target.value)}
                          className="min-w-40 rounded-lg bg-surface-2 px-2 py-1 text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                          disabled={updateLoading}
                        >
                          <option value="">{t('schoolDashboard.selectRegion')}</option>
                          {regionsList.map((region) => (
                            <option key={region} value={region}>{region}</option>
                          ))}
                        </select>
                      )}
                      <EditButtons onSave={handleSaveRegion} onCancel={handleCancelEdit} updateLoading={updateLoading} />
                    </>
                  }
                >
                  {overview?.region || '—'}
                </MetaField>

                {canEdit && (
                  <MetaField
                    label={t('schoolDashboard.additionalUrl')}
                    editing={isEditingAdditionalUrl}
                    canEdit={canEdit}
                    onStartEdit={handleStartEditAdditionalUrl}
                    editTitle={t('schoolDashboard.editAdditionalUrl')}
                    truncate
                    editor={
                      <>
                        <input
                          type="url"
                          value={editingAdditionalUrlValue}
                          onChange={(e) => setEditingAdditionalUrlValue(e.target.value)}
                          className="w-full min-w-0 rounded-lg bg-surface-2 px-2 py-1 text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                          disabled={updateLoading}
                          placeholder="https://example.com"
                        />
                        <EditButtons onSave={handleSaveAdditionalUrl} onCancel={handleCancelEdit} updateLoading={updateLoading} />
                      </>
                    }
                  >
                    {overview?.additional_url ? (
                      <a href={overview.additional_url} target="_blank" rel="noopener noreferrer" className="block max-w-xs truncate text-brand-300 hover:underline">
                        {overview.additional_url}
                      </a>
                    ) : '—'}
                  </MetaField>
                )}

                {canEdit && (
                  <MetaField
                    label={t('schoolDashboard.photoUrl')}
                    editing={isEditingPhotoUrl}
                    canEdit={canEdit}
                    onStartEdit={handleStartEditPhotoUrl}
                    editTitle={t('schoolDashboard.editPhotoUrl')}
                    truncate
                    editor={
                      <>
                        <input
                          type="url"
                          value={editingPhotoUrlValue}
                          onChange={(e) => setEditingPhotoUrlValue(e.target.value)}
                          className="w-full min-w-0 rounded-lg bg-surface-2 px-2 py-1 text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                          disabled={updateLoading}
                          placeholder="https://example.com/photo.jpg"
                        />
                        <EditButtons onSave={handleSavePhotoUrl} onCancel={handleCancelEdit} updateLoading={updateLoading} />
                      </>
                    }
                  >
                    {overview?.photo_url ? (
                      <a href={overview.photo_url} target="_blank" rel="noopener noreferrer" className="block max-w-xs truncate text-brand-300 hover:underline">
                        {overview.photo_url}
                      </a>
                    ) : '—'}
                  </MetaField>
                )}

                <MetaField label={t('schoolDashboard.stability')}>
                  <span className={
                    overview?.connectionStability?.type === 'stable' ? 'text-positive' :
                    overview?.connectionStability?.type === 'unstable' ? 'text-warning' :
                    overview?.connectionStability?.type === 'offline' ? 'text-critical' :
                    'text-muted-400'
                  }>
                    {overview?.connectionStability?.message || '—'}
                  </span>
                </MetaField>

                {canEdit && (
                  <div className="col-span-full mt-2 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setIsDeleteModalOpen(true)}
                      className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs text-muted-500 transition hover:bg-critical/10 hover:text-critical"
                    >
                      <span className="material-icons text-[16px]">delete</span>
                      {t('schoolDashboard.deleteSchool')}
                    </button>
                  </div>
                )}
              </div>
              )}

              {updateError && (
                <div className="mt-3 rounded-lg bg-critical/10 px-3 py-2 text-xs text-critical">{updateError}</div>
              )}
              {updateSuccess && (
                <div className="mt-3 rounded-lg bg-positive/10 px-3 py-2 text-xs text-positive">{updateSuccess}</div>
              )}

          <section className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <MapSection
                markers={markers}
                disableDefaultLayout
                className="h-[600px]"
                title={t('map.location')}
                subtitle={overview?.address || null}
              />
            </div>
            <div className="lg:col-span-7">
              <section className="flex h-[600px] flex-col rounded-3xl bg-surface-1 p-6">
                <header className="mb-4 flex min-h-9 flex-wrap items-center justify-between gap-3">
                  <h2 className="font-display text-lg text-white">{t('schoolDashboard.trafficHistory')}</h2>
                  <RangeSwitch ranges={defaultTimeRanges} value={historyRange} onChange={setHistoryRange} />
                </header>
                <div className="min-h-0 flex-1 rounded-3xl bg-surface-2 p-5">
                  <HistoryGraph chartData={historySeries} isLoading={historyLoading} heightClass="h-full" />
                </div>
              </section>
            </div>
          </section>

          <div id="school-equipment-nvr" className="scroll-mt-28">
            <HikvisionNvrSection objectIp={deviceIp} userRole={userRole} />
          </div>

          <div id="school-equipment-doors" className="scroll-mt-28">
            <HikvisionDoorsSection objectIp={deviceIp} userRole={userRole} />
          </div>

          <div id="school-equipment-panic" className="scroll-mt-28">
            <HikvisionPanicSection objectIp={deviceIp} userRole={userRole} />
          </div>

          <section className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="rounded-3xl bg-surface-1 p-6">
                <header className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="font-display text-lg text-white">{t('schoolDashboard.recentSpeedtests')}</h2>
                    <p className="text-sm text-muted-400">{t('schoolDashboard.connectionHistory')}</p>
                  </div>
                </header>
                <SpeedtestTable
                  results={speedtests}
                  isLoading={speedtestsLoading}
                  page={speedtestsPage}
                  totalPages={speedtestsTotalPages}
                  onPageChange={setSpeedtestsPage}
                  t={t}
                />
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-3xl bg-surface-1 p-6">
                <header className="mb-4 flex min-h-9 flex-wrap items-center justify-between gap-3">
                  <h2 className="font-display text-lg text-white">{t('schoolDashboard.schoolLogs')}</h2>
                  <RangeSwitch ranges={defaultTimeRanges} value={logsRange} onChange={setLogsRange} />
                </header>
                <LogsTable
                  logs={logs}
                  isLoading={logsLoading}
                  page={logsPage}
                  totalPages={logsTotalPages}
                  onPageChange={setLogsPage}
                  t={t}
                />
              </div>
            </div>
          </section>

          <AlertsTable
            alerts={alerts}
            isLoading={alertsLoading}
            onAcknowledge={acknowledgeAlert}
            acknowledgeLoading={acknowledgeLoading}
            page={alertsPage}
            totalPages={alertsTotalPages}
            pageSize={alertsPageSize}
            onPageChange={setAlertsPage}
            onChangePageSize={setAlertsPageSize}
            totalCount={alertsTotalCount}
            schoolName={overview?.school || state?.school}
          />
        </main>
      </div>

      <SpeedtestModalWebSocket
        isOpen={speedtestModalOpen}
        onClose={() => setSpeedtestModalOpen(false)}
        deviceName={overview?.school || state?.school || deviceIp}
        deviceIP={deviceIp}
      />

      <DeleteSchoolConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false)
          setDeleteError('')
        }}
        onConfirm={handleDeleteSchool}
        isLoading={isDeleting}
        schoolName={overview?.school || state?.school}
      />

      {/* Отображение ошибки удаления */}
      {deleteError && (
        <div className="fixed bottom-4 right-4 z-[1200] rounded-lg border border-critical/50 bg-critical/10 px-4 py-3 text-sm text-critical shadow-elevated">
          {deleteError}
        </div>
      )}
    </div>
  )
}

export default SchoolDashboard

